import { AfterContentInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { addDays, format, isAfter, isSameDay, parseISO, toDate } from 'date-fns'
import { of } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import {
    AmenitiesCriterion,
    AvailabilityType,
    BoundingBox,
    Criteria,
    PriceRangeCriterion,
    RegionCriterion,
    ScreenSize
} from '../../../../graphql'
import { defaultGuestRestrictions, searchListingsCriteriaKey } from '../../../constant'
import { searchCriteriaFromStorage, UnsubscribeOnDestroy } from '../../../utility'
import { SearchCriteriaFromStorage } from './search-criteria-from-storage'
import { TravelDates } from '../../../module/shared-member/organism/listing/shared/travel-dates'
import { Router } from '@angular/router'
import { SearchCriteriaWhereToMenuComponent } from './search-criteria-where-to-menu/search-criteria-where-to-menu'
import { SearchCriteriaGuestsComponent } from './search-criteria-guests/search-criteria-guests'
import { DatepickerComponent } from '../../../module/shared-member/organism/datepicker/datepicker'

@Component({
    selector: 'app-search-criteria',
    styleUrls: ['search-criteria.scss'],
    templateUrl: 'search-criteria.html'
})
export class SearchCriteriaComponent extends UnsubscribeOnDestroy implements AfterContentInit {
    
    private minimumDaysFuture = 7
  
    @Input() disabled = true
    @Input() minimumConsecutiveNights: number
    @Input() showMoreFilters = true
    @Input() showModalFilters = false
    @Input() travelDates: TravelDates
    @Input() layout: 'row' | 'col' = 'row'
    @Input() activeFilter: 'region' | 'date' | 'guests' | undefined
    @Input() maxGuests = 100
    @Input() aiSearchEnabled = false
    @Input() aiSearchInitWithValue: string

    @Output() criteria = new EventEmitter<Criteria>()
    @Output() setInitCriteria = new EventEmitter<Criteria>()
    @Output() activeRegion: EventEmitter<BoundingBox> = new EventEmitter<BoundingBox>()
    @Output() searchRegionName: EventEmitter<string> = new EventEmitter<string>()
    @Output() aiSearchActive: EventEmitter<boolean> = new EventEmitter<boolean>()

    @ViewChild(SearchCriteriaWhereToMenuComponent) searchRegion!: SearchCriteriaWhereToMenuComponent
    @ViewChild(DatepickerComponent) searchDates!: DatepickerComponent
    @ViewChild(SearchCriteriaGuestsComponent) searchGuests!: SearchCriteriaGuestsComponent

    futureDay: Date = addDays(toDate(new Date()), this.minimumDaysFuture)
    formGroup: FormGroup
    showAvailabilityType = false
    availabilityType: AvailabilityType
    includeComingSoon: boolean
    triggeredRegionUpdate = false
    guestRestrictions$ = of(defaultGuestRestrictions)
    aiSearchMode = false
    aiSearchText: string | undefined = undefined

    constructor(private router: Router) {
        
        super()

        this.formGroup = new FormGroup({
            amenitySmokeDetector: new FormControl(),
            // bed: new FormControl(this.defaultBeds(), Validators.required),
            checkin: new FormControl(this.defaultCheckin()),
            checkout: new FormControl(this.defaultCheckout()),
            facilitiesPool: new FormControl(),
            availabilityType: new FormControl(this.defaultAvailabilityType()),
            guests: new FormControl(this.defaultGuests(), [Validators.required, Validators.max(this.maxGuests)]),
            whereTo: new FormControl(this.defaultWhereTo()),
            aiSearchText: new FormControl('')
        })

        this._subscriptions.push(this.formGroup
            .valueChanges
            .pipe(
                // Use longer debounce time for AI search to prevent too frequent API calls
                debounceTime(this.aiSearchMode ? 1000 : 300)
            )
            .subscribe((event) => {
                this.search(this.defaultPaging())

                // If searching from route other than search-listings reroute.
                if (this.router.url !== '/search-listings') {
                    this.router.navigate(['/search-listings']);
                }
            }))

        this.includeComingSoon = this.formGroup.value?.availabilityType != AvailabilityType.Rental
    }

    ngAfterContentInit(): void {
        // TODO: Messy. On change of Rental/Exchange, reload the "Where to"
        let availabilityTypeControl = this.formGroup.controls.availabilityType
        this.availabilityType = availabilityTypeControl.value
        this.includeComingSoon = this.availabilityType != AvailabilityType.Rental

        this._subscriptions.push(availabilityTypeControl.valueChanges.subscribe(availabilityType => {
            this.availabilityType = null
            setTimeout(() => {
                this.availabilityType = availabilityType
                this.includeComingSoon = availabilityType != AvailabilityType.Rental
                this.search(this.defaultPaging())
            }, 1)
        }))

        setTimeout(() => this.search(this.defaultPaging(), true), 50)
    }

    newSearchDates(dates: { checkin: Date, checkout: Date }): void {
        if ((dates.checkin && dates.checkout) || (!dates.checkin && !dates.checkout)) {
            this.formGroup.patchValue({
                checkin: dates.checkin, 
                checkout: dates.checkout
            })
        }
    }

    // Public
    search(paging = { offset: 0, size: 50 }, init?: boolean) {
        if (!this.formGroup.valid) { return }
        if (this.aiSearchMode && !this.aiSearchText) { return }

        const storedCriteria = searchCriteriaFromStorage()
        const regionCriterion = this.formGroup.value?.whereTo?.regionId
            ? { regionId: this.formGroup.value.whereTo.regionId, subRegionId: this.formGroup.value.whereTo.subRegionId }
            : undefined
        const availabilityTypeCriterion = this.formGroup.value.availabilityType
            ? { availabilityType: this.formGroup.value.availabilityType }
            : undefined
        const criteria: Criteria = {
            guestCriterion: {
                adults: this.formGroup.value.guests.adults,
                children: this.formGroup.value.guests.children,
                infants: this.formGroup.value.guests.infants
            },
            priceRangeCriterion: this.defaultPriceRange(storedCriteria),
            roomsAndBedsCriterion: this.defaultRoomsAndBedsCriterion(storedCriteria),
            brandsCriterion: this.defaultBrandsCriterion(storedCriteria),
            amenitiesCriterion: this.defaultAmenitiesCriterion(storedCriteria),
            assetTypesCriterion: this.defaultAssetTypes(storedCriteria),
            styleCriterion: this.defaultStyleCriterion(storedCriteria),
            metadata: {
                screenSize: ScreenSize.Large,
                includeComingSoon: this.includeComingSoon
            },
            paging,
            availabilityTypeCriterion,
            regionCriterion,
            searchText: this.aiSearchMode ? this.aiSearchText : undefined
        }
        if (this.formGroup.value.checkin && this.formGroup.value.checkout) {
            criteria.datesCriterion = {
                checkin: format(this.formGroup.value.checkin, 'yyyy-MM-dd'),
                checkout: format(this.formGroup.value.checkout, 'yyyy-MM-dd')
            }
        } else {    
            criteria.datesCriterion = undefined

            criteria.availableFromCriterion = {
                minimumConsecutiveNights: this.minimumConsecutiveNights,
                start: addDays(new Date(), this.minimumDaysFuture)
            }
        }

        sessionStorage.setItem(searchListingsCriteriaKey, JSON.stringify(criteria))

        if (init) { 
            // On init only set criteria and then let map load trigger first search
            this.setInitCriteria.next(criteria)
        } else {
            this.criteria.next(criteria)
        }
    }

    toggleAiSearch($event: boolean) {
        if ($event !== this.aiSearchMode) {
            
            this.aiSearchMode = $event
            this.aiSearchActive.emit($event)
            
            // perform standard search to restore normal listing results
            if (!$event && this.aiSearchText) {
                this.search()
            }

            this.aiSearchText = undefined
        }
    }

    aiSearch($event: string) {
        if ($event) {
            const url = this.router.url.split('?')
            if (url[0] !== '/search-listings') {
                this.router.navigateByUrl(`/search-listings?aiSearchText=${$event}`)
            } else {
                this.aiSearchText = $event
                this.search() 
            }
        }
    }

    defaultCheckin(): Date {
        const criteria = searchCriteriaFromStorage()
        if (criteria.checkin) { 
            const checkin = parseISO(criteria.checkin)
            if (isSameDay(checkin, this.futureDay) || isAfter(checkin, this.futureDay)) {
                return checkin
            }
        }
    }

    defaultCheckout(): Date {
        const criteria = searchCriteriaFromStorage()
        if (criteria.checkout) {
            const checkout = parseISO(criteria.checkout)
            if (isAfter(checkout, this.futureDay)) {
                return checkout
            }
        }
    }

    defaultGuests(): any {
        const criteria = searchCriteriaFromStorage()
        if (criteria.guestCriterion) { return criteria.guestCriterion }

        return {
            adults: 1,
            children: 0,
            infants: 0
        }
    }

    defaultWhereTo(): RegionCriterion | undefined {
        const criteria = searchCriteriaFromStorage()
        return criteria.regionCriterion || { regionId: null }
    }

    defaultAvailabilityType(): AvailabilityType | undefined {
        const criteria = searchCriteriaFromStorage()
        if (criteria.availabilityTypeCriterion) {
            return criteria.availabilityTypeCriterion.availabilityType
        }
        return this.isConsumer() ? AvailabilityType.Rental : AvailabilityType.Exchange
    }

    defaultAssetTypes(criteria: SearchCriteriaFromStorage = searchCriteriaFromStorage()) {
        return {
            assetTypes: criteria?.assetTypesCriterion?.assetTypes ?? []
        }
    }

    defaultPriceRange(criteria: SearchCriteriaFromStorage = searchCriteriaFromStorage()) {
        return {
            minimum: criteria?.priceRangeCriterion?.minimum ?? {
                amountMicros: 0,
                currency: 'USD'
            },
            maximum: criteria?.priceRangeCriterion?.maximum ?? {
                amountMicros: 100000000000,
                currency: 'USD'
            }
        } as PriceRangeCriterion
    }

    defaultPaging(criteria: SearchCriteriaFromStorage = searchCriteriaFromStorage()): { offset: number, size: number } | undefined {
        return criteria.paging
    }

    clearTravelDates() {
        this.formGroup.controls.checkin.setValue(null)
        this.formGroup.controls.checkout.setValue(null)
    }

    resetSearchCriteria() {
        this.searchDates.updateCheckinDate(null)
        this.searchDates.updateCheckoutDate(null)
        this.searchRegion.searchAllDestinations()
        this.searchGuests.clearFilters('adults')
        this.searchGuests.clearFilters('children')
        this.searchGuests.clearFilters('infants')
    }

    resetSearchRegionMenu() {
        this.searchRegion.resetMenu()
    }

    isConsumer(): boolean {
        // return this._roleManager.hasRole(Role.Consumer)
        return true
    }

    canSeeExchangeProperties(canSeeExchangeProperties: boolean) {
        this.showAvailabilityType = canSeeExchangeProperties
    }

    onActiveRegionChange($event) {
        this.triggeredRegionUpdate = true
        this.activeRegion.next($event)
        
        setTimeout(() => {
            this.triggeredRegionUpdate = false
        }, 100)
    }

    onLocationSelected($event: BoundingBox) {
        this.activeRegion.next($event)
    }

    private defaultRoomsAndBedsCriterion(criteria: SearchCriteriaFromStorage = searchCriteriaFromStorage()) {
        return criteria.roomsAndBedsCriterion ?? {
            beds: 0,
            bathrooms: 0,
            bedrooms: 0,
        }
    }

    private defaultBrandsCriterion(criteria: SearchCriteriaFromStorage = searchCriteriaFromStorage()) {
        return criteria.brandsCriterion ?? {
            brandIds: [],
        }
    }

    private defaultStyleCriterion(criteria: SearchCriteriaFromStorage = searchCriteriaFromStorage()) {
        return criteria.styleCriterion ?? {
            propertyStyles: [],
        }
    }

    private defaultAmenitiesCriterion(criteria: SearchCriteriaFromStorage = searchCriteriaFromStorage()) {
        return criteria.amenitiesCriterion ?? {
            selectedAmenityIds: []
        } as AmenitiesCriterion
    }
}
